import { useState } from 'react';
import TableAffiliate, {
  CopyText,
  type TableColumn,
} from '@/domains/dashboard-management/components/TableAffiliate/TableAffiliate';
import UploadTransferProofModal from '@/domains/dashboard-management/components/TransferProofModal/UploadTransferProofModal';
import ViewTransferProofModal from '@/domains/dashboard-management/components/TransferProofModal/ViewTransferProofModal';
import {
  useAdminWithdrawals,
  useRecordAdminPayout,
} from '@/domains/dashboard-management/hooks/useAdminAffiliates';
import type { DuePayout, Withdrawal } from '@/domains/dashboard-management/types/affiliate.types';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import { withdrawalCode } from '@/domains/mobile-web/utils/withdrawal';
import Button from '@/shared/components/ui/Button';

const rupiah = (value: string | number) => `Rp ${formatPrice(Number(value))}`;

const STATUS = {
  pending: { label: 'Pending', className: 'text-[#FF9500]' },
  paid: { label: 'Selesai', className: 'text-[#038E60]' },
  rejected: { label: 'Ditolak', className: 'text-[#FF3B30]' },
  cancelled: { label: 'Dibatalkan', className: 'text-[#FF3B30]' },
};

/** One table row: the balance still due (Pending) or a paid withdrawal. */
interface HistoryRow {
  key: string;
  code: string;
  bankName: string | null;
  holder: string | null;
  accountNumber: string | null;
  amount: string;
  status: keyof typeof STATUS;
  /** Set on the Pending row only */
  due?: DuePayout;
  proofUrl?: string | null;
}

const dueRow = (due: DuePayout): HistoryRow => ({
  key: 'due',
  code: '-',
  bankName: due.bank_name,
  holder: due.bank_account_holder,
  accountNumber: due.bank_account_number,
  amount: due.amount,
  status: 'pending',
  due,
});

const paidRow = (withdrawal: Withdrawal): HistoryRow => ({
  key: withdrawal.id,
  code: withdrawalCode(withdrawal),
  bankName: withdrawal.bank_name,
  holder: withdrawal.bank_account_holder,
  accountNumber: withdrawal.bank_account_number,
  amount: withdrawal.amount,
  status:
    withdrawal.status === 'rejected' || withdrawal.status === 'cancelled'
      ? withdrawal.status
      : withdrawal.status === 'paid'
        ? 'paid'
        : 'pending',
  proofUrl: withdrawal.proof?.url,
});

interface WithdrawalHistoryProps {
  affiliateId: string;
  description?: string;
  className?: string;
}

/** Riwayat: the balance still to transfer (Sudah Transfer) and the paid withdrawals (Lihat Bukti Transfer). */
const WithdrawalHistory = ({ affiliateId, description, className }: WithdrawalHistoryProps) => {
  const { data, isLoading, isError } = useAdminWithdrawals(affiliateId);
  const payout = useRecordAdminPayout(affiliateId);
  const [payingAmount, setPayingAmount] = useState<string | null>(null);
  const [proofUrl, setProofUrl] = useState<string | null>(null);

  const rows = data ? [...(data.due ? [dueRow(data.due)] : []), ...data.rows.map(paidRow)] : [];

  const columns: TableColumn<HistoryRow>[] = [
    { key: 'code', label: 'ID', width: '110px' },
    {
      key: 'bankName',
      label: 'Bank Tujuan',
      width: '140px',
      render: (row) => row.bankName?.toUpperCase() ?? '-',
    },
    { key: 'holder', label: 'Nama Rekening', width: '180px' },
    {
      key: 'accountNumber',
      label: 'Nomor Rekening',
      width: '170px',
      render: (row) =>
        row.accountNumber ? (
          <CopyText value={row.accountNumber} label={`Salin nomor rekening ${row.code}`} />
        ) : (
          '-'
        ),
    },
    {
      key: 'amount',
      label: 'Jumlah Transfer',
      width: '160px',
      render: (row) => rupiah(row.amount),
    },
    {
      key: 'status',
      label: 'Status',
      width: '110px',
      render: (row) => (
        <span className={`body1medium ${STATUS[row.status].className}`}>
          {STATUS[row.status].label}
        </span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      width: '170px',
      align: 'center',
      render: (row) =>
        row.due ? (
          <Button
            customButton="btn-fill-blue h-9! px-3 body2medium"
            // No destination to transfer to until the member adds a payout account
            disabled={!row.due.bank_code}
            onClick={() => setPayingAmount(row.amount)}
          >
            Sudah Transfer
          </Button>
        ) : row.status === 'paid' ? (
          <Button
            customButton="btn-fill-white-db h-9! px-3 body2medium"
            disabled={!row.proofUrl}
            onClick={() => setProofUrl(row.proofUrl ?? null)}
          >
            Lihat Bukti Transfer
          </Button>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <>
      <TableAffiliate
        title="Riwayat"
        description={description}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        emptyText={
          isLoading
            ? 'Memuat...'
            : isError
              ? 'Gagal memuat riwayat.'
              : 'Belum ada riwayat transfer.'
        }
        className={className}
      />
      <UploadTransferProofModal
        open={payingAmount !== null}
        loading={payout.isPending}
        onUpload={(proof, previewUrl) => {
          // The modal hands its preview over; only the file is sent
          URL.revokeObjectURL(previewUrl);
          if (!payingAmount) return;
          payout.mutate(
            { amount: payingAmount, proof },
            // A changed balance (409) closes too, so the refreshed amount is checked again
            { onSettled: () => setPayingAmount(null) }
          );
        }}
        onClose={() => setPayingAmount(null)}
      />
      <ViewTransferProofModal
        open={proofUrl !== null}
        imageUrl={proofUrl}
        onClose={() => setProofUrl(null)}
      />
    </>
  );
};

export default WithdrawalHistory;
